import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, ref } from "vue";
import BaseModal from "@/components-UI/modals/BaseModal.vue";
import BaseAutocomplete from "@/components/base/design/BaseAutocomplete.vue";
import BaseDateTimePicker from "@/components-UI/BaseDateTimePicker.vue";
import CKEditor from '@ckeditor/ckeditor5-vue2';
import Editor from '@crm-ui/ckeditor';
import { getActiveUserSearch, getAffiliateSearch, getUserDepartments } from "@/api/user";
import { getFormatDate } from "@/utils/formatDates";
import { IBaseAutocomplete } from "@/types";
import { IBulkCreateTask, ICreateTask } from "@/api/types/tasks";
import { bulkCreateNewTask, createNewTask, createTasksForDepartment } from "@/api/tasks";
import { showNotification, showServerError } from "@/utils";
import { useWindowResize } from "@/utils/useWindowSize";
import TrashButton from "@/components-UI/buttons/TrashButton.vue";
import BaseSelect from "@/components-UI/BaseSelect.vue";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import { getEmployeesOffersV2 } from "@/api/offers";
import store from "@/store";
import BaseSwitch from "@/components-UI/BaseSwitch.vue";

type PropsTarget = 'offer' | 'affiliate';
type LocalTarget = 'employee' | PropsTarget;

interface Props {
    isShow: boolean;
    managerId?: number;
    target?: PropsTarget;
    targetId?: number | number[];
    isBulkCreate?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateTaskModal',
  props: {
    isShow: { type: Boolean },
    managerId: null,
    target: null,
    targetId: null,
    isBulkCreate: { type: Boolean }
  },
  emits: ["update:is-show", "update-list"],
  setup(__props: any, { emit }) {

const props = __props as {
    isShow: boolean;
    managerId?: number;
    target?: PropsTarget;
    targetId?: number | number[];
    isBulkCreate?: boolean;
}




const { width } = useWindowResize();
const isFullscreen = computed(() => width.value <= 768);

onBeforeMount(() => {
    setDefaultDate();
    setDefaultTarget();
});

const closeModal = () => {
    resetValue();
    emit('update:is-show', false);
};

const resetValue = () => {
    assignee.value = null;
    textValue.value = '';
    targetObject.value = null;
    isDepartment.value = false;
    department.value = null;

    setDefaultTarget();
    setDefaultDate();
};

const isNeedTargetFields = computed(() => !props.target && !props.targetId);
const isHideAssignee = computed(() => isNeedTargetFields.value && targetType.value === 'employee' && !isDepartment.value);

const isDisableApprove = computed(() => {
    if (isNeedTargetFields.value) {
        const common = !date.value || !textValue.value || !targetObject.value;
        switch (targetType.value) {
        case 'offer':
        case 'affiliate':
            return !assignee.value || common;
        case 'employee':
            return common;
        default:
            return true;
        }
    }
    const common = !date.value || !textValue.value;
    return props.isBulkCreate ? common : !assignee.value || common;
});

// -- target type
const targetType = ref<LocalTarget>('');

const targetItems: { text: string, value: LocalTarget }[] = computed(() => {
    const items = [
        { text: 'Сотрудник', value: 'employee' },
        { text: 'Оффер', value: 'offer' },
        { text: 'Партнёр', value: 'affiliate' },
    ];
    if (isDepartment.value) {
        return items.filter(item => item.value !== 'employee');
    }
    return items;
});

const selectTarget = (value: LocalTarget) => {
    targetType.value = value;
    targetObject.value = null;
};

// -- target object
const targetObject = ref(null);
const targetSelect = ref<BaseAutocomplete | null>(null);

const targetData = {
    affiliate: {
        apiMethod: getAffiliateSearch,
        errorText: 'Партнёр не найден',
        label: 'Объект',
        itemValue: 'id',
        cashItems: false,
        template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
    },
    employee: {
        apiMethod: getActiveUserSearch,
        errorText: 'Сотрудник не найден',
        label: 'Объект',
        itemValue: 'id',
        cashItems: false,
        template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
    },
    offer: {
        apiMethod: getEmployeesOffersV2,
        key: 'search',
        itemValue: 'legacy_id',
        template: (i) => `${i.legacy_id} ${i.name}`,
        errorText: 'Оффер не найден',
        label: 'Объект',
        cashItems: false,
        externalApiParams: (value: string) => ({
            search: value,
            fields: 'legacy_id,name',
        }),
    },
};

const getTargetData = computed(() => {
    const disabled = {
        disabled: true,
        label: 'Объект',
    };

    return targetData[targetType.value] ?? disabled;
});

const setDefaultTarget = () => {
    if (!isNeedTargetFields.value) return;
    targetType.value = 'employee';
    const user = store.getters['authModule/GET_USER'];
    targetObject.value = user.account_id;
};
const clearTarget = () => {
    targetObject.value = null;
    targetType.value = '';
    targetSelect.value?.clearModel();
    assigneeSelect.value?.clearModel();
};

// -- assignee
const assignee = ref(null);
const assigneeSelect = ref<BaseAutocomplete | null>(null);

const isDisableAssignee = computed(() => isDepartment.value && department.value === null);
const maxShowItems = computed(() => {
    if (width.value <= 576) return 1;
    return 3;
});

const getAssigneeData = computed(() => {
    const label = props.isBulkCreate
        ? props.target === 'affiliate' ? 'Менеджер партнёра' : 'Менеджер оффера'
        : 'Исполнитель';
    const errorText = props.isBulkCreate
        ? props.target === 'affiliate' ? 'Менеджер партнёра не найден' : 'Менеджер оффера не найден'
        : 'Исполнитель не найден';
    const assigneeData: IBaseAutocomplete = {
        apiMethod: getActiveUserSearch,
        errorText,
        label,
        itemValue: 'id',
        template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
        multiple: isDepartment.value,
        disabled: isDisableAssignee.value,
        cashItems: false,
        maxShowItems: maxShowItems.value,
    };
    return assigneeData;
});

// -- date
const date = ref('');
const setDefaultDate = () => {
    const tomorrow = new Date().setDate(new Date().getDate() + 1);
    date.value = getFormatDate(tomorrow, 'yyyy-MM-dd');
};
const updateDate = (value: string) => {
    date.value = value;
};

// -- text
const textValue = ref('');
const clearText = () => {
    textValue.value = '';
};

// -- department
const isDepartment = ref(false);
const department = ref(null);
const departmentItems = ref<{ text: string, value: number }[]>([]);
const departmentEmployees = ref([]);

const toggleDepartment = (event: boolean) => {
    isDepartment.value = !isDepartment.value;

    if (event && targetType.value === 'employee') {
        clearTarget();
    }

    if (isDepartment.value) {
        getDepartments();
    } else {
        clearDepartment();
    }
};
const getDepartments = async () => {
    try {
        const { data } = await getUserDepartments();
        departmentItems.value = data.map((i) => ({ text: i.name, value: i.id }));
    } catch (err) {
        showServerError(err, 'Отделы не загружены');
    }
};
const selectDepartment = (value: number | '') => {
    if (value === '') {
        clearDepartment();
    } else {
        department.value = value;
        getDepartmentEmployee();
    }
};

const clearDepartment = () => {
    department.value = null;
    assigneeSelect.value.items = [];
    assigneeSelect.value.model = [];
};

const getDepartmentEmployee = async () => {
    try {
        const { data } = await getActiveUserSearch({ departments: department.value });
        if (data && data.length) {
            assignee.value = data.map((i) => i.id);
            departmentEmployees.value = data;
            const items = data.map((i) => ({ name: `${i.id} ${i.first_name} ${i.last_name}`, value: i.id }));
            assigneeSelect.value.items = items;
            assigneeSelect.value.model = items;
        }
    } catch (err) {
        showServerError(err, 'Сотрудник не загружен');
    }
};

// -- create task
const isLoading = ref(false);
const createTask = async () => {
    const apiMethod = isDepartment.value ? createTasksForDepartment :
        props.isBulkCreate ? bulkCreateNewTask : createNewTask;
    const successText = props.isBulkCreate || isDepartment.value ? 'Задачи успешно созданы' : 'Задача успешно создана';
    const errorText = props.isBulkCreate || isDepartment.value ? 'Задачи не созданы' : 'Задача не создана';

    try {
        isLoading.value = true;
        const data = getDataForTaskCreate();
        await apiMethod(data);
        showNotification(successText);
        emit('update-list');
        closeModal();
    } catch (err) {
        showServerError(err, errorText);
    } finally {
        isLoading.value = false;
    }
};

const getDataForTaskCreate = () => {
    const data: ICreateTask | IBulkCreateTask = {
        text: textValue.value,
        assignee: targetType.value === 'employee' ? targetObject.value : assignee.value,
        target: props.target ?? targetType.value,
        date: date.value,
    };
    const key = props.isBulkCreate ? 'target_ids' : 'target_id';
    data[key] = props.targetId ?? targetObject.value;
    return data;
};

// -- editor
const editor = Editor;
const ckeditor = CKEditor.component;

const config = {
    placeholder: 'Введите текст задачи',
    language: 'ru',
    toolbar: {
        items: [
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            'fontColor',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'link',
            'horizontalLine',
            'imageUpload',
        ],
    },
    fontColor: {
        colorPicker: {
            // Use 'hex' format for output instead of 'hsl'.
            format: 'hex',
        },
        colors: [
            {
                color: '#0DA800',
                label: 'Green',
            },
            {
                color: '#F51E44',
                label: 'Red',
            },
        ],
        documentColors: 0,
    },
    forcePasteAsPlainText: false,
    extraAllowedContent: '*(*);*{*}',
    clipboard: {
        retainOnlyPasteData: false,
    },
    pasteFromWordRemoveFontStyles: false,
    pasteFromWordRemoveStyles: false,
    image: {
        resizeOptions: [],
        toolbar: [],
    },
    simpleUpload: {
        uploadUrl: '/api/tasks/v1/employees/image',
        headers: {
            Accept: '*/*',
        },
    },
};

return { __sfc: true,props, emit, width, isFullscreen, closeModal, resetValue, isNeedTargetFields, isHideAssignee, isDisableApprove, targetType, targetItems, selectTarget, targetObject, targetSelect, targetData, getTargetData, setDefaultTarget, clearTarget, assignee, assigneeSelect, isDisableAssignee, maxShowItems, getAssigneeData, date, setDefaultDate, updateDate, textValue, clearText, isDepartment, department, departmentItems, departmentEmployees, toggleDepartment, getDepartments, selectDepartment, clearDepartment, getDepartmentEmployee, isLoading, createTask, getDataForTaskCreate, editor, ckeditor, config, BaseModal, BaseAutocomplete, BaseDateTimePicker, TrashButton, BaseSelect, BaseTooltip, BaseSwitch }
}

})